// Avoid having an export on the first line for istanbul
import { FileTreeItemType } from '@dbt-labs/sourdough';

export const VIRTUAL_FOLDER_PREFIX = '__virtual__';

export const generateFolder = (
  relativePath: string,
  rootNodeId: string,
): FileTreeItemType => {
  const parts = relativePath.split('/');
  let name = parts.pop() || '';
  if (name.startsWith(VIRTUAL_FOLDER_PREFIX)) {
    name = `(Virtual) ${name.substring(VIRTUAL_FOLDER_PREFIX.length)}`;
  }
  const parent = parts.join('/') || rootNodeId;
  return {
    id: relativePath,
    parent,
    text: name,
    data: {
      pathType: 'directory',
      name,
      relativePath,
      depth: parts.length,
    },
  };
};

export const generateFile = (
  relativePath: string,
  rootNodeId: string,
): FileTreeItemType => {
  const parts = relativePath.split('/');
  const name = parts.pop() || '';
  const parent = parts.join('/') || rootNodeId;
  return {
    id: relativePath,
    parent,
    text: name,
    data: {
      pathType: 'file',
      name,
      relativePath,
      depth: parts.length,
    },
  };
};

/**
 * From a filepath, foo/bar/test.txt, generate the list:
 * ['foo', 'foo/bar', 'foo/bar/test.txt']
 */
export const getFolderHierarchy = (filePath: string | undefined) => {
  if (!filePath) return;
  const parts = filePath.split('/');
  const folders: string[] = [];
  let pathSoFar: string = '';
  parts.forEach((part) => {
    if (pathSoFar) {
      pathSoFar = `${pathSoFar}/${part}`;
    } else {
      pathSoFar = part;
    }
    folders.push(pathSoFar);
  });
  return folders;
};

export const isContainedInFolder = (
  folder: string,
  file: string | null | undefined,
): boolean => {
  folder = `${folder}/`.replaceAll('//', '/');
  return file === folder || Boolean(file?.startsWith(folder));
};

export const buildRelativePath = (...pathParts: string[]) => {
  return pathParts.join('/').replaceAll('//', '/');
};

export const getFileName = (filePath: string) => {
  const parts = filePath.split('/');
  return parts[parts.length - 1];
};

/**
 * Some file paths from dbt-core have a `${packageName}://` prefix which is not a valid path otherwise.
 * This will remove the prefix and optionally check that the passed-in `packageName` matches the string
 * (if present; returns `null` on a mismatch).
 */
export const sanitizeCoreFilePath = (
  filePath: string | null | undefined,
  packageName?: string,
): string | null => {
  if (filePath == null) {
    return null;
  }

  const sanitizedPath = filePath.replace(/^(\w+:\/\/)/, '');

  if (
    packageName !== undefined &&
    filePath.includes('://') &&
    !filePath.startsWith(`${packageName}://`)
  ) {
    return null;
  }

  return sanitizedPath;
};
